import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 2-1: overworld, lots of gaps, long stretches, koopas
export function buildLevel2_1(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [13, 14],
    [30, 33],
    [54, 57],
    [74, 77],
    [93, 96],
    [108, 111],
    [122, 125]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  // main ground
  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // early ?
  setTile(8, 4, TileType.Question, { kind: "mushroom" });

  // early blocks
  setTile(18, 4, TileType.Brick);
  setTile(19, 4, TileType.Brick);
  setTile(20, 4, TileType.Question, { kind: "coin" });
  setTile(21, 4, TileType.Brick);

  // pipes
  const pipes: Array<[number, number]> = [
    [25, 2],
    [42, 3],
    [48, 4],
    [82, 2],
    [100, 3],
    [130, 2],
    [140, 3],
    [152, 4],
    [168, 2],
    [178, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // high ?
  setTile(36, 7, TileType.Question, { kind: "coin" });
  setTile(62, 7, TileType.Question, { kind: "mushroom" });

  // after pit 3: block cluster
  setTile(58, 4, TileType.Brick);
  setTile(59, 4, TileType.Brick);
  setTile(60, 4, TileType.Brick);
  setTile(61, 4, TileType.Brick);
  setTile(62, 4, TileType.Brick);
  setTile(63, 4, TileType.Brick);
  setTile(64, 4, TileType.Brick);
  setTile(65, 4, TileType.Question, { kind: "coin" });

  // staircase 1
  stair(80, 1);
  stair(81, 2);
  stair(82, 3);
  stair(83, 4);
  stair(84, 4);
  stair(85, 4);
  stair(86, 3);
  stair(87, 2);
  stair(88, 1);

  // after pit 4
  setTile(98, 4, TileType.Brick);
  setTile(99, 4, TileType.Question, { kind: "coin" });
  setTile(100, 4, TileType.Brick);

  setTile(113, 4, TileType.Brick);
  setTile(114, 4, TileType.Brick);
  setTile(115, 4, TileType.Brick);
  setTile(116, 4, TileType.Brick);
  setTile(117, 4, TileType.Question, { kind: "coin" });

  // big brick structure
  for (let x = 130; x <= 138; x++) setTile(x, 4, TileType.Brick);
  setTile(133, 4, TileType.Question, { kind: "mushroom" });
  setTile(135, 4, TileType.Question, { kind: "coin" });
  setTile(140, 7, TileType.Brick);
  setTile(141, 7, TileType.Brick);
  setTile(142, 7, TileType.Brick);

  // before final pit
  setTile(155, 4, TileType.Brick);
  setTile(156, 4, TileType.Brick);
  setTile(157, 4, TileType.Brick);
  setTile(160, 4, TileType.Question, { kind: "coin" });
  setTile(163, 4, TileType.Brick);
  setTile(164, 4, TileType.Brick);
  setTile(165, 4, TileType.Brick);

  // final staircase up
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 10 },
    { kind: "goomba", x: 16 },
    { kind: "koopa", x: 22 },
    { kind: "goomba", x: 27 },
    { kind: "goomba", x: 35 },
    { kind: "goomba", x: 38 },
    { kind: "goomba", x: 44 },
    { kind: "koopa", x: 50 },
    { kind: "goomba", x: 58 },
    { kind: "goomba", x: 61 },
    { kind: "goomba", x: 68 },
    { kind: "koopa", x: 72 },
    { kind: "goomba", x: 79 },
    { kind: "goomba", x: 85 },
    { kind: "goomba", x: 90 },
    { kind: "goomba", x: 98 },
    { kind: "goomba", x: 102 },
    { kind: "koopa", x: 105 },
    { kind: "goomba", x: 113 },
    { kind: "goomba", x: 118 },
    { kind: "goomba", x: 123 },
    { kind: "goomba", x: 130 },
    { kind: "goomba", x: 135 },
    { kind: "koopa", x: 140 },
    { kind: "goomba", x: 145 },
    { kind: "goomba", x: 150 },
    { kind: "goomba", x: 156 },
    { kind: "goomba", x: 162 },
    { kind: "koopa", x: 170 },
    { kind: "goomba", x: 175 },
    { kind: "goomba", x: 180 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
